import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { MeasurementDefinition } from '@easydone/shared';

/**
 * Get all measurement definitions for an organization
 */
export async function getMeasurementDefinitions(
  orgId: string
): Promise<MeasurementDefinition[]> {
  if (!orgId) {
    console.error('getMeasurementDefinitions called with undefined orgId');
    return [];
  }

  const q = query(
    collection(db, 'organizations', orgId, 'measurementDefinitions'),
    orderBy('category', 'asc'),
    orderBy('name', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as MeasurementDefinition[];
}

/**
 * Get a single measurement definition by ID
 */
export async function getMeasurementDefinition(
  orgId: string,
  definitionId: string
): Promise<MeasurementDefinition | null> {
  const docRef = doc(db, 'organizations', orgId, 'measurementDefinitions', definitionId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as MeasurementDefinition;
}

/**
 * Create a new measurement definition
 */
export async function createMeasurementDefinition(
  orgId: string,
  definition: Omit<MeasurementDefinition, 'id'>
): Promise<string> {
  // Check for duplicate names in the same category
  const q = query(
    collection(db, 'organizations', orgId, 'measurementDefinitions'),
    where('name', '==', definition.name),
    where('category', '==', definition.category)
  );
  const existing = await getDocs(q);

  if (!existing.empty) {
    throw new Error(
      `A measurement definition named "${definition.name}" already exists in category "${definition.category}"`
    );
  }

  const docRef = await addDoc(collection(db, 'organizations', orgId, 'measurementDefinitions'), {
    ...definition,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  });

  return docRef.id;
}

/**
 * Update an existing measurement definition
 */
export async function updateMeasurementDefinition(
  orgId: string,
  definitionId: string,
  updates: Partial<Omit<MeasurementDefinition, 'id'>>
): Promise<void> {
  const docRef = doc(db, 'organizations', orgId, 'measurementDefinitions', definitionId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Measurement definition not found');
  }

  const data = docSnap.data();

  // If updating name or category, check for duplicates
  if (updates.name || updates.category) {
    const newName = updates.name || data.name;
    const newCategory = updates.category || data.category;

    const q = query(
      collection(db, 'organizations', orgId, 'measurementDefinitions'),
      where('name', '==', newName),
      where('category', '==', newCategory)
    );
    const existing = await getDocs(q);

    // Check if there's a duplicate (excluding the current document)
    const duplicates = existing.docs.filter((doc) => doc.id !== definitionId);
    if (duplicates.length > 0) {
      throw new Error(
        `A measurement definition named "${newName}" already exists in category "${newCategory}"`
      );
    }
  }

  await updateDoc(docRef, {
    ...updates,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a measurement definition
 */
export async function deleteMeasurementDefinition(
  orgId: string,
  definitionId: string
): Promise<void> {
  const docRef = doc(db, 'organizations', orgId, 'measurementDefinitions', definitionId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Measurement definition not found');
  }

  // Check if this definition is being used in any templates
  // (We'll need to implement this check when we update templates)

  await deleteDoc(docRef);
}

/**
 * Get measurement definitions by category
 */
export async function getMeasurementDefinitionsByCategory(
  orgId: string,
  category: string
): Promise<MeasurementDefinition[]> {
  const q = query(
    collection(db, 'organizations', orgId, 'measurementDefinitions'),
    where('category', '==', category),
    orderBy('name', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as MeasurementDefinition[];
}

/**
 * Get measurement definitions by context (floc, asset, or both)
 */
export async function getMeasurementDefinitionsByContext(
  orgId: string,
  context: 'floc' | 'asset' | 'both'
): Promise<MeasurementDefinition[]> {
  // Get all definitions where context matches or is 'both'
  const q = query(
    collection(db, 'organizations', orgId, 'measurementDefinitions'),
    orderBy('category', 'asc'),
    orderBy('name', 'asc')
  );

  const snapshot = await getDocs(q);
  const allDefs = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as MeasurementDefinition[];

  // Filter by context
  return allDefs.filter(
    (def) => def.context === context || def.context === 'both'
  );
}

/**
 * Get all unique categories
 */
export async function getMeasurementCategories(orgId: string): Promise<string[]> {
  const definitions = await getMeasurementDefinitions(orgId);
  const categories = new Set(definitions.map((def) => def.category || 'Uncategorized'));
  return Array.from(categories).sort();
}
